import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { pt } from 'date-fns/locale';
import { 
  BarChart3, 
  Car, 
  Gauge,
  Fuel,
  Clock,
  Calendar,
  Users,
  TrendingUp,
  ChevronLeft,
  ChevronRight,
  FileText
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';

const MONTHS = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
];

export default function ShiftReportsPage() {
  const { api, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('daily');
  
  // Daily report state
  const [selectedDate, setSelectedDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [dailyReport, setDailyReport] = useState(null);
  
  // Monthly report state
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [monthlyReport, setMonthlyReport] = useState(null);

  const isAdmin = user?.role === 'admin' || user?.role === 'gestor';

  useEffect(() => {
    if (activeTab === 'daily') {
      loadDailyReport();
    } else {
      loadMonthlyReport();
    }
  }, [activeTab, selectedDate, selectedMonth, selectedYear]);

  const loadDailyReport = async () => {
    setLoading(true);
    try {
      const response = await api().get(`/shifts/reports/daily?date=${selectedDate}`);
      setDailyReport(response.data);
    } catch (error) {
      toast.error('Erro ao carregar relatório diário');
    } finally {
      setLoading(false);
    }
  };

  const loadMonthlyReport = async () => {
    setLoading(true);
    try {
      const response = await api().get(`/shifts/reports/monthly?month=${selectedMonth}&year=${selectedYear}`);
      setMonthlyReport(response.data);
    } catch (error) {
      toast.error('Erro ao carregar relatório mensal');
    } finally {
      setLoading(false);
    }
  };

  const changeDate = (days) => {
    const date = new Date(selectedDate);
    date.setDate(date.getDate() + days);
    setSelectedDate(format(date, 'yyyy-MM-dd'));
  };

  const changeMonth = (delta) => {
    let newMonth = selectedMonth + delta;
    let newYear = selectedYear;
    
    if (newMonth > 12) {
      newMonth = 1;
      newYear++;
    } else if (newMonth < 1) {
      newMonth = 12;
      newYear--;
    }
    
    setSelectedMonth(newMonth);
    setSelectedYear(newYear);
  };

  const formatDuration = (startTime, endTime) => {
    if (!startTime || !endTime) return '-';
    const start = parseISO(startTime);
    const end = parseISO(endTime);
    const diff = Math.floor((end - start) / 1000 / 60);
    const hours = Math.floor(diff / 60);
    const mins = diff % 60;
    return `${hours}h ${mins}m`;
  };

  return (
    <div className="space-y-6" data-testid="shift-reports-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <BarChart3 className="h-6 w-6 text-amber-500" />
        <div>
          <h1 className="text-2xl font-bold font-[Manrope]">Relatórios de Turnos</h1>
          <p className="text-muted-foreground text-sm">Análise de quilómetros, combustível e viagens</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="daily" className="flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            Diário
          </TabsTrigger>
          <TabsTrigger value="monthly" className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            Mensal
          </TabsTrigger>
        </TabsList>

        {/* Daily Report */}
        <TabsContent value="daily" className="space-y-6">
          {/* Date Selector */}
          <div className="flex items-center justify-center gap-4">
            <Button variant="outline" size="icon" onClick={() => changeDate(-1)}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <div className="text-center min-w-[200px]">
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="text-lg font-semibold bg-transparent border-0 text-center cursor-pointer"
              />
              <p className="text-sm text-muted-foreground">
                {format(new Date(selectedDate), "EEEE", { locale: pt })}
              </p>
            </div>
            <Button variant="outline" size="icon" onClick={() => changeDate(1)}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500" />
            </div>
          ) : dailyReport ? (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 text-muted-foreground mb-1">
                      <Clock className="h-4 w-4" />
                      <span className="text-xs">Turnos</span>
                    </div>
                    <p className="text-2xl font-bold">{dailyReport.summary.total_shifts}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 text-muted-foreground mb-1">
                      <Gauge className="h-4 w-4" />
                      <span className="text-xs">Quilómetros</span>
                    </div>
                    <p className="text-2xl font-bold text-amber-600">{dailyReport.summary.total_km.toLocaleString()}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 text-muted-foreground mb-1">
                      <Fuel className="h-4 w-4" />
                      <span className="text-xs">Combustível</span>
                    </div>
                    <p className="text-2xl font-bold">{dailyReport.summary.total_fuel_liters} L</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 text-muted-foreground mb-1">
                      <Users className="h-4 w-4" />
                      <span className="text-xs">Visitas</span>
                    </div>
                    <p className="text-2xl font-bold text-emerald-600">{dailyReport.summary.total_visits}</p>
                  </CardContent>
                </Card>
              </div>

              {/* Shifts Table */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Turnos do Dia</CardTitle>
                </CardHeader>
                <CardContent>
                  {dailyReport.shifts.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <FileText className="h-12 w-12 mx-auto mb-4 opacity-30" />
                      <p>Nenhum turno registado neste dia</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>VENDEDOR</TableHead>
                            <TableHead>VIATURA</TableHead>
                            <TableHead>HORÁRIO</TableHead>
                            <TableHead>KM</TableHead>
                            <TableHead>COMBUSTÍVEL</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {dailyReport.shifts.map((shift) => (
                            <TableRow key={shift.id}>
                              <TableCell className="font-medium">{shift.user_name}</TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <Car className="h-4 w-4 text-amber-500" />
                                  <div>
                                    <p className="text-sm">{shift.vehicle_brand} {shift.vehicle_model}</p>
                                    <p className="text-xs font-mono text-amber-600">{shift.vehicle_plate}</p>
                                  </div>
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="text-sm">
                                  <p>{format(parseISO(shift.start_time), "HH:mm")} - {shift.end_time ? format(parseISO(shift.end_time), "HH:mm") : 'Em curso'}</p>
                                  <p className="text-xs text-muted-foreground">{formatDuration(shift.start_time, shift.end_time)}</p>
                                </div>
                              </TableCell>
                              <TableCell>
                                <div>
                                  <p className="font-semibold">{shift.km_total?.toLocaleString() || '-'} km</p>
                                  <p className="text-xs text-muted-foreground">{shift.km_start?.toLocaleString()} → {shift.km_end?.toLocaleString() || '?'}</p>
                                </div>
                              </TableCell>
                              <TableCell>
                                {shift.fuel_added ? (
                                  <Badge className="bg-amber-100 text-amber-800">
                                    <Fuel className="h-3 w-3 mr-1" />
                                    {shift.fuel_liters} L
                                  </Badge>
                                ) : (
                                  <span className="text-muted-foreground text-sm">-</span>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          ) : null}
        </TabsContent>

        {/* Monthly Report */}
        <TabsContent value="monthly" className="space-y-6">
          {/* Month Selector */}
          <div className="flex items-center justify-center gap-4">
            <Button variant="outline" size="icon" onClick={() => changeMonth(-1)}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <div className="text-center min-w-[200px]">
              <p className="text-lg font-semibold">{MONTHS[selectedMonth - 1]} {selectedYear}</p>
            </div>
            <Button variant="outline" size="icon" onClick={() => changeMonth(1)}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500" />
            </div>
          ) : monthlyReport ? (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 text-muted-foreground mb-1">
                      <Clock className="h-4 w-4" />
                      <span className="text-xs">Turnos</span>
                    </div>
                    <p className="text-2xl font-bold">{monthlyReport.summary.total_shifts}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 text-muted-foreground mb-1">
                      <Gauge className="h-4 w-4" />
                      <span className="text-xs">Total Km</span>
                    </div>
                    <p className="text-2xl font-bold text-amber-600">{monthlyReport.summary.total_km.toLocaleString()}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 text-muted-foreground mb-1">
                      <TrendingUp className="h-4 w-4" />
                      <span className="text-xs">Média/Turno</span>
                    </div>
                    <p className="text-2xl font-bold">{monthlyReport.summary.avg_km_per_shift} km</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 text-muted-foreground mb-1">
                      <Fuel className="h-4 w-4" />
                      <span className="text-xs">Combustível</span>
                    </div>
                    <p className="text-2xl font-bold">{monthlyReport.summary.total_fuel_liters} L</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 text-muted-foreground mb-1">
                      <span className="text-xs">€</span>
                      <span className="text-xs">Custo Comb.</span>
                    </div>
                    <p className="text-2xl font-bold text-red-600">{monthlyReport.summary.total_fuel_cost.toFixed(2)}€</p>
                  </CardContent>
                </Card>
              </div>

              {/* Daily Chart */}
              {monthlyReport.daily_breakdown.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Quilómetros por Dia</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-[250px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={monthlyReport.daily_breakdown}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis 
                            dataKey="date" 
                            tickFormatter={(val) => format(new Date(val), 'd')}
                            fontSize={12}
                          />
                          <YAxis fontSize={12} />
                          <Tooltip 
                            labelFormatter={(val) => format(new Date(val), "d MMM", { locale: pt })}
                            formatter={(value) => [`${value} km`, 'Quilómetros']}
                          />
                          <Bar dataKey="km" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* By User Table */}
              {isAdmin && monthlyReport.by_user.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Users className="h-4 w-4 text-amber-500" />
                      Por Vendedor
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>VENDEDOR</TableHead>
                          <TableHead>TURNOS</TableHead>
                          <TableHead>KM TOTAL</TableHead>
                          <TableHead>COMBUSTÍVEL</TableHead>
                          <TableHead>VISITAS</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {monthlyReport.by_user.map((userStats, idx) => (
                          <TableRow key={idx}>
                            <TableCell className="font-medium">{userStats.user_name}</TableCell>
                            <TableCell>{userStats.total_shifts}</TableCell>
                            <TableCell className="font-semibold text-amber-600">{userStats.total_km.toLocaleString()} km</TableCell>
                            <TableCell>{userStats.total_fuel} L</TableCell>
                            <TableCell>{userStats.total_visits}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}

              {/* By Vehicle Table */}
              {monthlyReport.by_vehicle.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Car className="h-4 w-4 text-amber-500" />
                      Por Viatura
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>VIATURA</TableHead>
                          <TableHead>MATRÍCULA</TableHead>
                          <TableHead>TURNOS</TableHead>
                          <TableHead>KM TOTAL</TableHead>
                          <TableHead>COMBUSTÍVEL</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {monthlyReport.by_vehicle.map((vehicleStats, idx) => (
                          <TableRow key={idx}>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <Car className="h-4 w-4 text-amber-500" />
                                {vehicleStats.vehicle}
                              </div>
                            </TableCell>
                            <TableCell className="font-mono text-amber-600">{vehicleStats.plate}</TableCell>
                            <TableCell>{vehicleStats.total_shifts}</TableCell>
                            <TableCell className="font-semibold">{vehicleStats.total_km.toLocaleString()} km</TableCell>
                            <TableCell>{vehicleStats.total_fuel} L</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}
            </>
          ) : null}
        </TabsContent>
      </Tabs>
    </div>
  );
}
